import { SHTableRow } from "@components/design-systems";
import { FeatureAnalysisRowDTO } from "@models/platform-analysis/entities/steps/feature";
import { Column, Row } from "@tanstack/react-table";
import { VirtualItem, Virtualizer } from "@tanstack/react-virtual";
import { memo, useCallback } from "react";
import { TableVirtualizationConfigs } from "../../../configs";
import { AnalysisTableStickyColumns } from "../sticky-columns";
import { AnalysisTableVirtualizedColumns } from "../virtualized-columns";

type AnalysisTableRowProps = {
  row: Row<FeatureAnalysisRowDTO>;
  virtualRow: VirtualItem<Element>;
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  columns: Column<FeatureAnalysisRowDTO>[];
  columnVirtualizerItems: VirtualItem<Element>[];
  isRowExpanded: boolean;
};

export const AnalysisTableRow = memo(
  ({
    row,
    virtualRow,
    rowVirtualizer,
    columns,
    columnVirtualizerItems,
    isRowExpanded,
  }: AnalysisTableRowProps) => {
    const setRowRef = useCallback(
      (node: HTMLTableRowElement | null) => {
        if (node) rowVirtualizer.measureElement(node);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [rowVirtualizer, virtualRow],
    );

    return (
      <SHTableRow
        data-index={virtualRow.index}
        ref={setRowRef}
        sx={{
          display: "flex",
          position: "absolute",
          transform: `translateY(${virtualRow.start}px)`,
          width: "100%",
          minHeight: TableVirtualizationConfigs.DefaultRowHeight,
          height: "auto",
          border: "none",
          bgcolor: "transparent",
        }}
      >
        <AnalysisTableStickyColumns
          row={row}
          columns={columns}
          isRowExpanded={isRowExpanded}
        />

        <AnalysisTableVirtualizedColumns
          row={row}
          columnVirtualizerItems={columnVirtualizerItems}
        />
      </SHTableRow>
    );
  },
);
